import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Alert from '../../Alert';
import Modal from '../../Modal';
import Mediapicker from '../../Mediapicker';
import HelpArticleLink from '../HelpArticleLink';
import niceNameForShippingMethod from '../Shipping/niceNameForShippingMethod';
import useDebouncedAutosave from './useDebouncedAutosave';
import './ProductWizardStock.css';
import { variantTypes } from 'mayhem-taxonomy';

const DOWNLOAD_TYPES = ['digital'];
const MAX_PRODUCT_QUANTITY = 99;
// Product types that can carry a per-variant shipping override, mirroring
// `isShippableProduct` in the SSR editor (server/views/dashboard/product-variant.ejs).
const SHIPPABLE_PRODUCT_TYPES = ['physical', 'commission', 'subscription'];
// Carrier / generic / pickup groupings for the per-variant shipping override.
// Kept in sync with VARIANT_SHIPPING_METHODS in server/helpers/productVariants.js
// (the server-side allowlist the normalizer enforces) and the SSR editor's fieldsets.
const VARIANT_SHIPPING_CARRIER_METHODS = [
    'USPS_GROUND_ADVANTAGE', 'PRIORITY_MAIL', 'UPS_GROUND', 'UPS_3_DAY_SELECT',
];
const VARIANT_SHIPPING_GENERIC_METHODS = ['GENERIC_GROUND', 'GENERIC_PRIORITY'];
const VARIANT_SHIPPING_RATED_METHODS = [
    ...VARIANT_SHIPPING_CARRIER_METHODS, ...VARIANT_SHIPPING_GENERIC_METHODS,
];
const CUSTOM_LABEL = '__custom_label__';
const labelOptions = (variants) => [...new Set([...variantTypes, ...variants.map(v => v.type).filter(Boolean)])];

function VariantLabelSelect({id, value, onChange, customValue, onCustomChange, options}) {
    return <>
        <select id={id} value={value} onChange={e => onChange(e.target.value)}>
            {options.map(label => <option key={label} value={label}>{label}</option>)}
            <option value={CUSTOM_LABEL}>Custom…</option>
        </select>
        {value === CUSTOM_LABEL && <>
            <label htmlFor={`${id}-custom`}>Custom label</label>
            <input id={`${id}-custom`} value={customValue} onChange={e => onCustomChange(e.target.value)} required />
        </>}
    </>;
}
const DEFAULT_NEW_VARIANT = {
    type: 'Size',
    name: '',
    names: '',
    price: '',
    stock: '',
    fileId: null,
    isRelativePrice: false,
};

function formatPrice(cents) {
    return cents != null ? `$${(cents / 100).toFixed(2)}` : '—';
}

function formatAdjustment(cents) {
    const sign = cents > 0 ? '+' : cents < 0 ? '-' : '';
    return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function parseVariantNames(newVariant, isBulkMode) {
    if (isBulkMode) {
        return newVariant.names
            .split(/\r?\n/)
            .map(name => name.trim())
            .filter(Boolean);
    }

    const singleName = newVariant.name.trim();
    return singleName ? [singleName] : [];
}

function StockCell({ variantKey, stock, onSetStock, onUnstock }) {
    const isTracked = typeof stock[variantKey] !== 'undefined';
    const [input, setInput] = useState(isTracked ? String(stock[variantKey]) : '');
    const [saving, setSaving] = useState(false);

    // Sync if stock changes externally (e.g. after reload)
    useEffect(() => {
        if (isTracked) setInput(String(stock[variantKey]));
    }, [stock[variantKey]]);

    const handleSet = async () => {
        setSaving(true);
        await onSetStock(variantKey, parseInt(input, 10) || 0);
        setSaving(false);
    };

    const handleUnstock = async () => {
        setSaving(true);
        await onUnstock(variantKey);
        setSaving(false);
    };

    if (!isTracked) {
        return (
            <button type="button" className="small button secondary" onClick={handleSet} disabled={saving}>
                Track
            </button>
        );
    }

    return (
        <div className="stock-cell-controls">
            <input
                type="number"
                value={input}
                min={0}
                onChange={e => setInput(e.target.value)}
                className="stock-cell-input"
                aria-label={variantKey === 'general' ? 'General stock' : `Stock for variant ${variantKey}`}
            />
            <button type="button" className="small button" onClick={handleSet} disabled={saving}>
                {saving ? 'Saving…' : 'Update'}
            </button>
            <button type="button" className="small button secondary" onClick={handleUnstock} disabled={saving}>
                Unlimited
            </button>
        </div>
    );
}

function GeneralStockSelector({ stock, onSetStock, onUnstock }) {
    const currentStock = stock.general;
    const initialChoice = typeof currentStock === 'undefined'
        ? 'unlimited'
        : (currentStock === 1 ? 'one-of-a-kind' : 'limited');
    const [choice, setChoice] = useState(initialChoice);
    const [input, setInput] = useState(
        typeof currentStock === 'undefined' ? '' : String(currentStock),
    );
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (typeof currentStock === 'undefined') {
            setChoice('unlimited');
            setInput('');
        } else {
            setChoice(currentStock === 1 ? 'one-of-a-kind' : 'limited');
            setInput(String(currentStock));
        }
    }, [currentStock]);

    const handleChoice = async (nextChoice) => {
        setChoice(nextChoice);

        if (nextChoice === 'limited') return;

        setSaving(true);
        if (nextChoice === 'one-of-a-kind') {
            setInput('1');
            if (currentStock !== 1) await onSetStock('general', 1);
        } else {
            setInput('');
            if (typeof currentStock !== 'undefined') await onUnstock('general');
        }
        setSaving(false);
    };

    const handleLimitedSubmit = async (event) => {
        event.preventDefault();
        const amount = Number(input);
        if (!Number.isInteger(amount) || amount < 0) return;

        setSaving(true);
        await onSetStock('general', amount);
        setSaving(false);
    };

    const choices = [
        {
            value: 'one-of-a-kind',
            title: 'One of a kind',
            description: 'Only one is available. Stock is set to 1.',
        },
        {
            value: 'unlimited',
            title: 'Unlimited',
            description: 'Keep selling without tracking a quantity.',
        },
        {
            value: 'limited',
            title: 'Limited stock',
            description: 'Enter the exact number currently available.',
        },
    ];

    return (
        <div className="general-stock-selector">
            <p>Choose how many of this product are available.</p>
            <div className="general-stock-card-grid">
                {choices.map(option => {
                    const selected = choice === option.value;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            className={`general-stock-choice-card${selected ? ' selected' : ''}`}
                            onClick={() => handleChoice(option.value)}
                            aria-pressed={selected}
                            disabled={saving}
                        >
                            <strong>{option.title}</strong>
                            <span>{option.description}</span>
                        </button>
                    );
                })}
            </div>
            {choice === 'limited' && (
                <form className="general-stock-limited" onSubmit={handleLimitedSubmit}>
                    <div className="group">
                        <label htmlFor="general-stock-amount">Number available</label>
                        <input
                            id="general-stock-amount"
                            type="number"
                            value={input}
                            min="0"
                            step="1"
                            required
                            onChange={event => setInput(event.target.value)}
                        />
                    </div>
                    <button type="submit" className="button small" disabled={saving}>
                        {saving ? 'Saving…' : 'Save Stock'}
                    </button>
                </form>
            )}
        </div>
    );
}

function VariantFileCell({ variant, onSaveFile }) {
    const [fileId, setFileId] = useState(variant.fileId || null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setFileId(variant.fileId || null);
    }, [variant.fileId]);

    const handleSave = async () => {
        setSaving(true);
        await onSaveFile(variant.uuid, fileId);
        setSaving(false);
    };

    return (
        <div className="variant-file-editor">
            <Mediapicker
                name={`variant-file-${variant.uuid}`}
                initialValue={fileId}
                onChange={setFileId}
            />
            <button type="button" className="small button" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save File'}
            </button>
        </div>
    );
}

function VariantPriceCell({ variant, basePrice, onSavePrice }) {
    const [input, setInput] = useState(variant.price != null ? (variant.price / 100).toFixed(2) : '');
    const [isRelativePrice, setIsRelativePrice] = useState(variant.isRelativePrice === true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setInput(variant.price != null ? (variant.price / 100).toFixed(2) : '');
        setIsRelativePrice(variant.isRelativePrice === true);
    }, [variant.price, variant.isRelativePrice]);

    const handleSave = async () => {
        setSaving(true);
        await onSavePrice(variant.uuid, input.trim(), isRelativePrice);
        setSaving(false);
    };

    return (
        <div className="variant-price-editor">
            <input
                type="number"
                value={input}
                onChange={e => setInput(e.target.value)}
                step="0.01"
                min={isRelativePrice ? undefined : '0'}
                placeholder={basePrice}
                aria-label={`Price for ${variant.name}`}
            />
            <label className="checkbox">
                <input
                    type="checkbox"
                    checked={isRelativePrice}
                    onChange={e => setIsRelativePrice(e.target.checked)}
                    aria-label={`Price is an adjustment for ${variant.name}`}
                />
                <span>Price is an adjustment</span>
            </label>
            <button type="button" className="small button" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Price'}
            </button>
            <p className="help variant-price-help">
                {input.trim() === ''
                    ? (isRelativePrice ? '$0.00 adjustment' : `${basePrice} (base price)`)
                    : (isRelativePrice
                        ? `${formatAdjustment(Math.round(parseFloat(input) * 100))} adjustment`
                        : 'Full variant price. Leave blank and save to use the base price.')}
            </p>
        </div>
    );
}

// Build the initial working shipping-form shape from a variant's stored override.
// The stored shape is what normalizeVariantShipping produces server-side; scalar
// numeric fields come back as-is and `allowCombinedShipping` as a real boolean, so
// coerce it to the string the <select> uses.
function initialVariantShippingForm(variant) {
    const s = variant.shipping && typeof variant.shipping === 'object' ? variant.shipping : {};
    return {
        mode: s.mode || '',
        methods: Array.isArray(s.methods) ? [...s.methods] : [],
        rates: s.rates && typeof s.rates === 'object' ? { ...s.rates } : {},
        originZIPCode: s.originZIPCode || '',
        pickupAddress: s.pickupAddress || '',
        weight: s.weight ?? '',
        length: s.length ?? '',
        width: s.width ?? '',
        height: s.height ?? '',
        allowCombinedShipping: typeof s.allowCombinedShipping === 'boolean'
            ? String(s.allowCombinedShipping)
            : (s.allowCombinedShipping || ''),
        secondaryRate: s.secondaryRate ?? '',
    };
}

function variantHasShippingOverride(variant) {
    return !!(variant.shipping && typeof variant.shipping === 'object'
        && Object.keys(variant.shipping).length > 0);
}

// Compact per-variant shipping override. Mirrors the SSR editor's conditional
// visibility (server/views/dashboard/product-variant.ejs toggle script) and
// produces the raw shape the v1 variants API runs through normalizeVariantShipping.
function VariantShippingOverride({ shipping, onChange }) {
    const mode = shipping.mode || '';
    const methods = Array.isArray(shipping.methods) ? shipping.methods : [];
    const rates = shipping.rates && typeof shipping.rates === 'object' ? shipping.rates : {};

    const setField = (field, value) => onChange({ [field]: value });
    const toggleMethod = (method) => {
        const next = methods.includes(method)
            ? methods.filter(m => m !== method)
            : [...methods, method];
        onChange({ methods: next });
    };
    const setRate = (method, value) => onChange({ rates: { ...rates, [method]: value } });

    const genericDisabled = mode === 'calculated';
    const showPickupAddress = mode === 'pickup' || methods.includes('LOCAL_PICKUP');
    const showCombined = mode === 'flat' || mode === 'calculated';
    const showSecondaryRate = showCombined && shipping.allowCombinedShipping === 'true';
    const ratedMethods = mode === 'flat'
        ? VARIANT_SHIPPING_RATED_METHODS.filter(m => methods.includes(m))
        : [];

    return (
        <fieldset className="variant-shipping-override">
            <legend>Shipping for this variant</legend>
            <div className="group">
                <label htmlFor="variant-shipping-mode">Shipping pricing</label>
                <select
                    id="variant-shipping-mode"
                    value={mode}
                    onChange={e => setField('mode', e.target.value)}
                >
                    <option value="">Use product setting</option>
                    <option value="none">No shipping calculation — included in product price</option>
                    <option value="flat">Simple flat rate shipping</option>
                    <option value="calculated">Calculated shipping based on customer ZIP</option>
                    <option value="pickup">For pick-up only</option>
                </select>
            </div>

            <fieldset className="variant-shipping-methods">
                <legend>Carrier methods</legend>
                {VARIANT_SHIPPING_CARRIER_METHODS.map(method => (
                    <label key={method} className="checkbox">
                        <input
                            type="checkbox"
                            checked={methods.includes(method)}
                            onChange={() => toggleMethod(method)}
                        />
                        <span>{niceNameForShippingMethod(method)}</span>
                    </label>
                ))}
            </fieldset>
            <fieldset className="variant-shipping-methods">
                <legend>Generic methods (flat rate only)</legend>
                {VARIANT_SHIPPING_GENERIC_METHODS.map(method => (
                    <label key={method} className="checkbox">
                        <input
                            type="checkbox"
                            checked={methods.includes(method)}
                            disabled={genericDisabled}
                            onChange={() => toggleMethod(method)}
                        />
                        <span>{niceNameForShippingMethod(method)}</span>
                    </label>
                ))}
            </fieldset>
            <fieldset className="variant-shipping-methods">
                <legend>Pickup</legend>
                <label className="checkbox">
                    <input
                        type="checkbox"
                        checked={methods.includes('LOCAL_PICKUP')}
                        onChange={() => toggleMethod('LOCAL_PICKUP')}
                    />
                    <span>{niceNameForShippingMethod('LOCAL_PICKUP')}</span>
                </label>
            </fieldset>

            <div className="group">
                <label htmlFor="variant-shipping-origin">ZIP code you're shipping from</label>
                <input
                    id="variant-shipping-origin"
                    type="text"
                    value={shipping.originZIPCode || ''}
                    onChange={e => setField('originZIPCode', e.target.value)}
                    placeholder="eg: 12345"
                />
            </div>

            {showPickupAddress && (
                <div className="group">
                    <label htmlFor="variant-shipping-pickup">Pickup address</label>
                    <input
                        id="variant-shipping-pickup"
                        type="text"
                        value={shipping.pickupAddress || ''}
                        onChange={e => setField('pickupAddress', e.target.value)}
                        placeholder="eg: 123 Main St, City, State, ZIP"
                    />
                </div>
            )}

            <div className="group">
                <label htmlFor="variant-shipping-weight">Package weight (lb)</label>
                <input
                    id="variant-shipping-weight"
                    type="number"
                    min="0"
                    step="0.1"
                    value={shipping.weight ?? ''}
                    onChange={e => setField('weight', e.target.value)}
                />
            </div>
            <div className="split">
                {['length', 'width', 'height'].map(dim => (
                    <div className="group" key={dim}>
                        <label htmlFor={`variant-shipping-${dim}`}>
                            Package {dim} (in)
                        </label>
                        <input
                            id={`variant-shipping-${dim}`}
                            type="number"
                            min="0"
                            step="0.1"
                            value={shipping[dim] ?? ''}
                            onChange={e => setField(dim, e.target.value)}
                        />
                    </div>
                ))}
            </div>

            {ratedMethods.length > 0 && (
                <div className="variant-shipping-rates">
                    {ratedMethods.map(method => (
                        <div className="group" key={method}>
                            <label htmlFor={`variant-shipping-rate-${method}`}>
                                Rate for {niceNameForShippingMethod(method)}
                            </label>
                            <input
                                id={`variant-shipping-rate-${method}`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={rates[method] || ''}
                                onChange={e => setRate(method, e.target.value)}
                            />
                        </div>
                    ))}
                </div>
            )}

            {showCombined && (
                <div className="group">
                    <label htmlFor="variant-shipping-combined">Allow combined shipping?</label>
                    <select
                        id="variant-shipping-combined"
                        value={shipping.allowCombinedShipping || ''}
                        onChange={e => setField('allowCombinedShipping', e.target.value)}
                    >
                        <option value="">Use product setting</option>
                        <option value="false">No, this item must ship separately</option>
                        <option value="true">Yes, combine shipping for multiple items</option>
                    </select>
                </div>
            )}
            {showSecondaryRate && (
                <div className="group">
                    <label htmlFor="variant-shipping-secondary">Secondary shipping rate</label>
                    <input
                        id="variant-shipping-secondary"
                        type="number"
                        min="0"
                        step="0.01"
                        value={shipping.secondaryRate ?? ''}
                        onChange={e => setField('secondaryRate', e.target.value)}
                    />
                    <p className="help">
                        Used for additional items after the highest shipping rate when
                        combining shipping.
                    </p>
                </div>
            )}
        </fieldset>
    );
}

function VariantEditSubpane({ variant, variants, saveError, isShippable, onSave, onClose }) {
    const [name, setName] = useState(variant.name || '');
    const [type, setType] = useState(variant.type || variantTypes[0]);
    const [localError, setLocalError] = useState('');
    const [saving, setSaving] = useState(false);

    const hadOverride = variantHasShippingOverride(variant);
    const [overrideEnabled, setOverrideEnabled] = useState(hadOverride);
    const [shipping, setShipping] = useState(() => initialVariantShippingForm(variant));
    const patchShipping = patch => setShipping(prev => ({ ...prev, ...patch }));

    const [customType, setCustomType] = useState('');
    const typeOptions = labelOptions(variants);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            setLocalError('Variant name is required.');
            return;
        }
        setLocalError('');

        const payload = { name: trimmedName, type: type === CUSTOM_LABEL ? customType : type };
        if (isShippable) {
            if (overrideEnabled) {
                // Send the raw working shape — the v1 API runs it through the
                // shared normalizeVariantShipping allowlist server-side.
                payload.shipping = shipping;
            } else if (hadOverride) {
                // Toggled off from an existing override -> explicit clear.
                payload.shipping = null;
            }
            // Never had an override and still not overriding -> omit `shipping`
            // entirely so a name/type-only save stays a name/type-only request.
        }

        setSaving(true);
        const ok = await onSave(variant.uuid, payload);
        setSaving(false);
        if (ok) onClose();
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={`Edit variant: ${variant.name}`}
            size="small"
            ariaLabelledBy="variant-edit-title"
        >
            <form className="variant-edit-subpane" onSubmit={handleSubmit}>
                {(localError || saveError) && <Alert type="error">{localError || saveError}</Alert>}
                <div className="group">
                    <label htmlFor="variant-edit-type">Variation Type</label>
                    <VariantLabelSelect id="variant-edit-type" value={type} onChange={setType}
                        customValue={customType} onCustomChange={setCustomType} options={typeOptions} />
                </div>
                <div className="group">
                    <label htmlFor="variant-edit-name">Variant Name</label>
                    <input
                        id="variant-edit-name"
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                    />
                </div>
                {isShippable && (
                    <div className="group">
                        <label htmlFor="variant-shipping-override-enabled">
                            Shipping settings override
                        </label>
                        <select
                            id="variant-shipping-override-enabled"
                            value={overrideEnabled ? 'true' : 'false'}
                            onChange={e => setOverrideEnabled(e.target.value === 'true')}
                        >
                            <option value="false">Use product shipping settings</option>
                            <option value="true">Override shipping for this variant</option>
                        </select>
                        <p className="help">
                            Use this when a variant ships differently — a different package
                            size, shipping method, pickup-only option, or flat rate.
                        </p>
                    </div>
                )}
                {isShippable && overrideEnabled && (
                    <VariantShippingOverride shipping={shipping} onChange={patchShipping} />
                )}
                <div className="group">
                    <button type="submit" className="button primary" disabled={saving}>
                        {saving ? 'Saving…' : 'Save Variant'}
                    </button>
                    <button type="button" className="button secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                </div>
            </form>
        </Modal>
    );
}

export default function ProductWizardStock({
    product,
    onNext,
    onBack,
    onSaveAndExit,
    onAutosave,
    error: wizardError,
}) {
    const { fetch, API_URL, channel } = useAuth();
    const isPrintify = product?.metadata?.isPrintify;
    const isManualBilling = channel?.billingMode === 'manual';
    const isDigital = DOWNLOAD_TYPES.includes(product?.type);

    const [variants, setVariants] = useState([]);
    const [stock, setStock] = useState({});
    const [quantityState, setQuantityState] = useState(product?.quantity || null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState('');
    const [buyMultiples, setBuyMultiples] = useState(product?.settings?.buyMultiples === true);
    const [maxQuantity, setMaxQuantity] = useState(product?.settings?.maxQuantity || 2);
    const [savingQuantity, setSavingQuantity] = useState(false);
    const quantityPayload = {
        settings: {
            buyMultiples,
            maxQuantity: buyMultiples ? Number(maxQuantity) : 1,
        },
    };
    const {cancelAutosave} = useDebouncedAutosave(quantityPayload, onAutosave, {
        enabled: Boolean(product?.uuid),
    });

    const [editingVariant, setEditingVariant] = useState(null);

    const [newVariant, setNewVariant] = useState(DEFAULT_NEW_VARIANT);
    const [customType, setCustomType] = useState('');
    const [renameGroup, setRenameGroup] = useState(null);
    const [renameTo, setRenameTo] = useState('');
    const [renameError, setRenameError] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [isBulkMode, setIsBulkMode] = useState(false);
    const [addError, setAddError] = useState('');
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        loadData();
    }, [product?.uuid]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [varRes, stockRes] = await Promise.all([
                fetch(`${API_URL}/v1/seller/products/${product.uuid}/variants`),
                fetch(`${API_URL}/v1/seller/products/${product.uuid}/stock`),
            ]);
            const [varJson, stockJson] = await Promise.all([varRes.json(), stockRes.json()]);
            if (!varRes.ok || !stockRes.ok) {
                throw new Error(
                    varJson.error || varJson.message || stockJson.error || stockJson.message
                    || 'Failed to load stock and variants.',
                );
            }
            if (varJson.data) setVariants(varJson.data);
            if (stockJson.data) setStock(stockJson.data);
            if (stockJson.meta?.quantity) {
                setQuantityState(stockJson.meta.quantity);
                setBuyMultiples(stockJson.meta.quantity.buyMultiples);
                setMaxQuantity(stockJson.meta.quantity.maxQuantity);
            }
        } catch (e) {
            setError(e.message || 'Failed to load stock and variants.');
        } finally {
            setLoading(false);
        }
    };

    const handleRenameGroup = async (event) => {
        event.preventDefault();
        setRenaming(true);
        setRenameError('');
        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/variants/rename-label`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({from: renameGroup, to: renameTo}),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || json.error || 'Failed to rename variant group.');
            await loadData();
            setRenameGroup(null);
            flash('Variant group renamed.');
        } catch (error) {
            setRenameError(error.message);
        } finally {
            setRenaming(false);
        }
    };

    const handleSetStock = async (variantKey, amount) => {
        setError(null);
        const body = variantKey === 'general' ? { stock: amount } : { variant: variantKey, stock: amount };
        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/stock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const json = await res.json();
            if (!res.ok) {
                setError(json.error || json.message || 'Failed to update stock');
                return;
            }
            setStock(json.data);
            if (json.meta?.quantity) {
                setQuantityState(json.meta.quantity);
                setBuyMultiples(json.meta.quantity.buyMultiples);
                setMaxQuantity(json.meta.quantity.maxQuantity);
            }
            flash('Stock updated.');
        } catch (e) {
            setError(e.message);
        }
    };

    const handleUnstock = async (variantKey) => {
        setError(null);
        const body = variantKey === 'general' ? {} : { variant: variantKey };
        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/stock/unstock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(json.error || json.message || 'Failed to remove stock tracking');
                return;
            }
            const newStock = { ...stock };
            if (variantKey === 'general') delete newStock.general;
            else delete newStock[variantKey];
            setStock(newStock);
            if (json.meta?.quantity) setQuantityState(json.meta.quantity);
            flash('Set to unlimited.');
        } catch (e) {
            setError(e.message);
        }
    };

    const handleSaveQuantitySettings = async (event) => {
        event.preventDefault();
        cancelAutosave();
        setError(null);
        setSavingQuantity(true);
        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...quantityPayload,
                }),
            });
            const json = await res.json();
            if (!res.ok) {
                throw new Error(json.error || json.message || 'Failed to save quantity settings');
            }
            flash('Purchase quantity settings saved.');
        } catch (e) {
            setError(e.message);
        } finally {
            setSavingQuantity(false);
        }
    };

    const handleDeleteVariant = async (variantUuid) => {
        setError('');
        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/variants/${variantUuid}/delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                const json = await res.json().catch(() => ({}));
                setError(json.error || json.message || 'Failed to delete variant');
                return;
            }
            await loadData();
        } catch (e) {
            setError(e.message);
        }
    };

    const handleUpdateVariantFile = async (variantUuid, fileId) => {
        setError(null);
        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/variants/${variantUuid}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fileId: fileId || null }),
            });
            const json = await res.json();
            if (!res.ok) {
                setError(json.error || json.message || 'Failed to update variant file');
                return;
            }
            setVariants(prev => prev.map(v => v.uuid === variantUuid ? { ...v, ...json.data } : v));
            flash('Variant file updated.');
        } catch (e) {
            setError(e.message);
        }
    };

    // Save the fields the edit subpane owns (name, type, and optionally a
    // per-variant shipping override). Price/file/stock keep their inline editors.
    // The v1 update handler writes only the keys it receives, so this leaves
    // every other variant field intact. Returns true on success so the subpane
    // closes.
    const handleUpdateVariantDetails = async (variantUuid, payload) => {
        setError(null);
        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/variants/${variantUuid}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(json.error || json.message || 'Failed to update variant');
                return false;
            }
            setVariants(prev => prev.map(v => v.uuid === variantUuid ? { ...v, ...json.data } : v));
            flash('Variant updated.');
            return true;
        } catch (e) {
            setError(e.message);
            return false;
        }
    };

    const handleUpdateVariantPrice = async (variantUuid, priceInput, isRelativePrice) => {
        setError(null);

        if (priceInput !== '' && (Number.isNaN(parseFloat(priceInput))
            || (!isRelativePrice && parseFloat(priceInput) < 0))) {
            setError('Variant price must be a valid number.');
            return;
        }

        try {
            const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/variants/${variantUuid}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    price: priceInput === '' ? null : parseFloat(priceInput),
                    isRelativePrice,
                }),
            });
            const json = await res.json();
            if (!res.ok) {
                setError(json.error || json.message || 'Failed to update variant price');
                return;
            }

            setVariants(prev => prev.map(variant => (
                variant.uuid === variantUuid
                    ? { ...variant, ...json.data }
                    : variant
            )));
            flash(priceInput === '' && !isRelativePrice
                ? 'Variant now uses the base price.'
                : 'Variant price updated.');
        } catch (e) {
            setError(e.message);
        }
    };

    const createVariant = async (name) => {
        const body = {
            name,
            type: newVariant.type === CUSTOM_LABEL ? customType : newVariant.type,
            isRelativePrice: newVariant.isRelativePrice,
        };
        if (newVariant.price !== '') body.price = parseFloat(newVariant.price);
        if (isDigital && newVariant.fileId) body.fileId = newVariant.fileId;

        const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/variants`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || json.message || 'Failed to add variant');

        return json.data;
    };

    const setInitialVariantStock = async (variantUuid) => {
        if (newVariant.stock === '' || !variantUuid) {
            return;
        }

        const res = await fetch(`${API_URL}/v1/seller/products/${product.uuid}/stock`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ variant: variantUuid, stock: parseInt(newVariant.stock, 10) }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || json.message || 'Failed to set initial stock');
    };

    const handleAddVariant = async (e) => {
        e.preventDefault();
        setAddError('');
        const variantNames = parseVariantNames(newVariant, isBulkMode);

        if (!variantNames.length) {
            setAddError(isBulkMode ? 'Enter at least one variant name.' : 'Variant name is required.');
            return;
        }

        if (newVariant.price !== '' && (Number.isNaN(parseFloat(newVariant.price))
            || (!newVariant.isRelativePrice && parseFloat(newVariant.price) < 0))) {
            setAddError('Variant price must be a valid number.');
            return;
        }

        if (newVariant.stock !== '' && (Number.isNaN(parseInt(newVariant.stock, 10)) || parseInt(newVariant.stock, 10) < 0)) {
            setAddError('Initial stock must be a whole number of 0 or more.');
            return;
        }

        setAdding(true);
        try {
            let createdCount = 0;
            const failures = [];

            for (const variantName of variantNames) {
                try {
                    const created = await createVariant(variantName);
                    await setInitialVariantStock(created?.uuid);
                    createdCount += 1;
                } catch (variantError) {
                    failures.push(`${variantName}: ${variantError.message}`);
                }
            }

            if (createdCount > 0) {
                setNewVariant(prev => ({
                    ...prev,
                    name: '',
                    names: '',
                    price: '',
                    stock: '',
                    isRelativePrice: false,
                }));
                await loadData();
                flash(createdCount === 1 ? 'Variant added.' : `${createdCount} variants added.`);
            }

            if (failures.length > 0) {
                setAddError(`Some variants could not be added: ${failures.join('; ')}`);
            }
        } catch (e) {
            setAddError(e.message);
        } finally {
            setAdding(false);
        }
    };

    const flash = (msg) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 3000);
    };

    const hasVariants = variants.length > 0;
    const isOneOfAKind = quantityState?.isOneOfAKind === true;
    const basePrice = formatPrice(product?.price);

    const quantityControls = (
        <section className="product-stock-quantity-controls">
            <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                <h3>Purchase Quantity</h3>
                <HelpArticleLink
                    slug="product-quantity-selection"
                    label="Help with quantity selection"
                    variant="icon"
                />
            </div>
            {isOneOfAKind ? (
                <p className="help">
                    One-of-a-kind products can only be purchased one at a time.
                </p>
            ) : (
                <form onSubmit={handleSaveQuantitySettings}>
                    <div className="group">
                        <label className="checkbox" htmlFor="buy-multiples">
                            <input
                                type="checkbox"
                                id="buy-multiples"
                                checked={buyMultiples}
                                onChange={e => setBuyMultiples(e.target.checked)}
                            />
                            <span>Allow shoppers to buy multiple quantities</span>
                        </label>
                    </div>
                    {buyMultiples && (
                        <div className="group">
                            <label htmlFor="max-purchase-quantity">
                                Maximum per order
                            </label>
                            <input
                                type="number"
                                id="max-purchase-quantity"
                                min="2"
                                max={MAX_PRODUCT_QUANTITY}
                                step="1"
                                required
                                value={maxQuantity}
                                onChange={e => setMaxQuantity(e.target.value)}
                            />
                            <p className="help">
                                Shoppers will also be limited by available stock.
                            </p>
                        </div>
                    )}
                    <button type="submit" className="button small" disabled={savingQuantity}>
                        {savingQuantity ? 'Saving…' : 'Save Quantity Settings'}
                    </button>
                </form>
            )}
        </section>
    );

    return (
        <div className="wizard-step">
            {loading ? (
                <p>Loading…</p>
            ) : (
                <>
                    {((error && !editingVariant) || wizardError) && <Alert type="error">{error || wizardError}</Alert>}
                    {successMsg && <Alert type="success">{successMsg}</Alert>}

                    {isManualBilling && (
                        <div className="flash info" style={{marginBottom: '1rem'}}>
                            <strong>Basic Plan note:</strong> Stock is reduced only when you manually mark an order as paid. Multiple customers may order the same item before you do so — mark orders as paid promptly to keep levels accurate.
                        </div>
                    )}

                    {hasVariants ? (
                        <section>
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                                <h3>Variants &amp; Stock</h3>
                                <HelpArticleLink
                                    slug="relative-variant-pricing"
                                    label="Help with relative variant pricing"
                                    variant="icon"
                                />
                            </div>
                            {[...new Set(variants.map(v => v.type).filter(Boolean))].map(label => (
                                <p key={label}>{label}{' '}
                                    <button type="button" className="small button secondary"
                                        onClick={() => { setRenameGroup(label); setRenameTo(label); setRenameError(''); }}>
                                        Rename {label}
                                    </button>
                                </p>
                            ))}
                            <table style={{width: '100%'}}>
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Name</th>
                                        <th>Price</th>
                                        {isDigital && <th>Digital File</th>}
                                        {!isDigital && <th>Stock</th>}
                                        {!isPrintify && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {variants.map(v => (
                                        <tr key={v.uuid}>
                                            <td data-label="Type">{v.type}</td>
                                            <td data-label="Name">{v.name}</td>
                                            <td data-label="Price">
                                                {isPrintify ? (
                                                    v.price != null
                                                        ? formatPrice(v.price)
                                                        : <em>{basePrice} (base)</em>
                                                ) : (
                                                    <VariantPriceCell
                                                        variant={v}
                                                        basePrice={basePrice}
                                                        onSavePrice={handleUpdateVariantPrice}
                                                    />
                                                )}
                                            </td>
                                            {isDigital && (
                                                <td data-label="Digital File">
                                                    <VariantFileCell
                                                        variant={v}
                                                        onSaveFile={handleUpdateVariantFile}
                                                    />
                                                </td>
                                            )}
                                            {!isDigital && (
                                                <td data-label="Stock">
                                                    {isPrintify ? (
                                                        <span>N/A (Printify)</span>
                                                    ) : (
                                                        <StockCell
                                                            variantKey={v.uuid}
                                                            stock={stock}
                                                            onSetStock={handleSetStock}
                                                            onUnstock={handleUnstock}
                                                        />
                                                    )}
                                                </td>
                                            )}
                                            {!isPrintify && (
                                                <td>
                                                    <div className="variant-row-actions">
                                                        <button
                                                            type="button"
                                                            className="small button secondary"
                                                            onClick={() => setEditingVariant(v)}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="variant-row-actions__delete"
                                                            onClick={() => handleDeleteVariant(v.uuid)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    ) : (
                        isDigital ? (
                            <section>
                                <h3>General Stock</h3>
                                <p>Digital products have unlimited availability &mdash; stock tracking is not applicable.</p>
                            </section>
                        ) : (
                            <section>
                                <h3>General Stock</h3>
                                <GeneralStockSelector
                                    stock={stock}
                                    onSetStock={handleSetStock}
                                    onUnstock={handleUnstock}
                                />
                            </section>
                        )
                    )}

                    {quantityControls}

                    <hr style={{margin: '1.5rem 0'}} />

                    {isPrintify ? (
                        <div className="flash warning">
                            Variants cannot be added to Printify products here. Manage them from your Printify dashboard.
                        </div>
                    ) : (
                        <section>
                            <h3>Add Variant</h3>
                            {hasVariants && typeof stock.general !== 'undefined' && (
                                <p className="help">Adding a variant will stop tracking general stock and track stock per-variant instead.</p>
                            )}
                            <form onSubmit={handleAddVariant}>
                                <div className="group">
                                    <label htmlFor="variant-add-type">Variation Type</label>
                                    <VariantLabelSelect id="variant-add-type" value={newVariant.type}
                                        onChange={type => setNewVariant(v => ({...v, type}))}
                                        customValue={customType} onCustomChange={setCustomType} options={labelOptions(variants)} />
                                    <p className="help">Choose a label or enter a custom label. Combine options into one variant when they share stock tracking.</p>
                                </div>
                                <div className="group">
                                    <label className="checkbox">
                                        <input
                                            type="checkbox"
                                            checked={isBulkMode}
                                            onChange={e => setIsBulkMode(e.target.checked)}
                                        />
                                        Add multiple variant names at once
                                    </label>
                                    <p className="help">When enabled, enter one variant name per line. Variants will be created one at a time in the order listed.</p>
                                </div>
                                <div className="group">
                                    <label htmlFor={isBulkMode ? 'variant-names' : 'variant-name'}>
                                        {isBulkMode ? 'Variant Names' : 'Variant Name'}
                                    </label>
                                    {isBulkMode ? (
                                        <textarea
                                            id="variant-names"
                                            value={newVariant.names}
                                            onChange={e => setNewVariant(v => ({ ...v, names: e.target.value }))}
                                            placeholder={'One variant name per line\nSilver\nGold\nRose Gold'}
                                            rows={6}
                                        />
                                    ) : (
                                        <input
                                            id="variant-name"
                                            type="text"
                                            value={newVariant.name}
                                            onChange={e => setNewVariant(v => ({ ...v, name: e.target.value }))}
                                            placeholder="e.g. Silver, Large, Cotton"
                                            required
                                        />
                                    )}
                                </div>
                                {isDigital && (
                                    <div className="group">
                                        <label>Digital File (optional)</label>
                                        <Mediapicker
                                            name="variant-file-new"
                                            initialValue={newVariant.fileId}
                                            onChange={val => setNewVariant(v => ({ ...v, fileId: val || null }))}
                                        />
                                        <p className="help">The file delivered when this variant is purchased. Leave blank to use the product's main file.</p>
                                    </div>
                                )}
                                <div className="group">
                                    <label htmlFor="new-variant-price">Price (optional, in dollars)</label>
                                    <input
                                        id="new-variant-price"
                                        type="number"
                                        value={newVariant.price}
                                        onChange={e => setNewVariant(v => ({ ...v, price: e.target.value }))}
                                        step="0.01"
                                        min={newVariant.isRelativePrice ? undefined : '0'}
                                        placeholder="Leave blank to use base price"
                                    />
                                    <label className="checkbox">
                                        <input
                                            type="checkbox"
                                            checked={newVariant.isRelativePrice}
                                            aria-label="New variant price is an adjustment"
                                            onChange={e => setNewVariant(v => ({
                                                ...v,
                                                isRelativePrice: e.target.checked,
                                            }))}
                                        />
                                        <span>Price is an adjustment</span>
                                    </label>
                                    <p className="help">
                                        {newVariant.isRelativePrice
                                            ? 'Adds or subtracts this amount after the highest selected full-price variant. Leave blank for a $0.00 adjustment.'
                                            : 'Sets the full price for this variant. Leave blank to use the product base price.'}
                                    </p>
                                </div>
                                <div className="group">
                                    <label>Initial Stock (optional)</label>
                                    <input
                                        type="number"
                                        value={newVariant.stock}
                                        onChange={e => setNewVariant(v => ({ ...v, stock: e.target.value }))}
                                        step="1"
                                        min="0"
                                        placeholder="Leave blank for unlimited"
                                    />
                                </div>
                                {addError && <Alert type="error">{addError}</Alert>}
                                <div className="group">
                                    <button type="submit" className="button primary" disabled={adding}>
                                        {adding ? 'Adding…' : isBulkMode ? 'Add Variants' : 'Add Variant'}
                                    </button>
                                </div>
                            </form>
                        </section>
                    )}
                </>
            )}

            {renameGroup !== null && <Modal isOpen onClose={() => setRenameGroup(null)} title={`Rename ${renameGroup}`} size="small">
                <form onSubmit={handleRenameGroup}>
                    {renameError && <Alert type="error">{renameError}</Alert>}
                    <label htmlFor="variant-group-label">New group label</label>
                    <input id="variant-group-label" value={renameTo} onChange={e => setRenameTo(e.target.value)} required />
                    <p className="help">Updates every option in this group. Shoppers with this product in their cart will need to select their options again.</p>
                    <button type="submit" disabled={renaming}>{renaming ? 'Renaming…' : 'Rename group'}</button>
                </form>
            </Modal>}
            {editingVariant && (
                <VariantEditSubpane
                    variant={editingVariant}
                    variants={variants}
                    saveError={error}
                    isShippable={SHIPPABLE_PRODUCT_TYPES.includes(product?.type)}
                    onSave={handleUpdateVariantDetails}
                    onClose={() => setEditingVariant(null)}
                />
            )}

            <div className="wizard-nav">
                <button type="button" className="button" onClick={onBack}>
                    ← Back
                </button>
                <button type="button" className="button secondary" onClick={() => onSaveAndExit(null)}>
                    Save and Edit Later
                </button>
                <button type="button" className="button primary" onClick={() => onNext(null)}>
                    Next →
                </button>
            </div>
        </div>
    );
}
